/**
 * Symbol library — 14 electrical component definitions.
 */
import type { SymbolDef } from '../types/symbol';
import upbLeaManifestRaw from './upb-lea.json';

const UPB = upbLeaManifestRaw as Record<string, { width: number; height: number; content: string }>;

const S = {
  stroke: '#0f172a',
  strokeDim: '#475569',
  fill: '#ffffff',
  fillTint: '#fafbfc',
  dcPos: '#c62828',
  badgeBlue: '#1e40af',
  badgeBlueBg: '#dbeafe',
  badgeAmber: '#92400e',
  badgeAmberBg: '#fef3c7'
};

function upb(name: keyof typeof UPB): string {
  return UPB[name].content.replace(/stroke:#000000/g, `stroke:${S.stroke}`);
}

export const SYMBOLS: Record<string, SymbolDef> = {

  inverter: {
    id: 'inverter',
    source: 'upb-lea',
    label: 'Inverter',
    category: 'inverter',
    nativeBox: { x: 0, y: 0, w: UPB.DC_AC.width, h: UPB.DC_AC.height },
    defaultScale: 4.5,
    // Mirror the glyph horizontally so the visible AC label aligns
    // with the actual wire entry expected by autoLayout (panels enter
    // from the right, AC bus exits below).
    content: `<g transform="scale(-1 1) translate(${-UPB.DC_AC.width} 0)">${upb('DC_AC')}</g>`,
    terminals: {
      dc_in:  { id: 'dc_in',  x: UPB.DC_AC.width, y: UPB.DC_AC.height / 2, type: 'dc' },
      ac_out: { id: 'ac_out', x: UPB.DC_AC.width / 2, y: UPB.DC_AC.height,  type: 'ac' }
    }
  },

  hybridInverter: {
    id: 'hybridInverter',
    source: 'upb-lea',
    label: 'Hybrid Inverter',
    category: 'inverter',
    nativeBox: { x: 0, y: 0, w: UPB.DC_AC_insulation.width, h: UPB.DC_AC_insulation.height },
    defaultScale: 4.5,
    content: `<g transform="scale(-1 1) translate(${-UPB.DC_AC_insulation.width} 0)">${upb('DC_AC_insulation')}</g>`,
    terminals: {
      dc_in:    { id: 'dc_in',    x: UPB.DC_AC_insulation.width, y: UPB.DC_AC_insulation.height * 0.3, type: 'dc' },
      ac_out:   { id: 'ac_out',   x: UPB.DC_AC_insulation.width / 2, y: UPB.DC_AC_insulation.height, type: 'ac' },
      bat_port: { id: 'bat_port', x: UPB.DC_AC_insulation.width, y: UPB.DC_AC_insulation.height * 0.75, type: 'dc' }
    }
  },

  dcdcConverter: {
    id: 'dcdcConverter',
    source: 'upb-lea',
    label: 'DC-DC Converter',
    category: 'inverter',
    nativeBox: { x: 0, y: 0, w: UPB.DC_DC.width, h: UPB.DC_DC.height },
    defaultScale: 3.5,
    content: upb('DC_DC'),
    terminals: {
      dc_in:  { id: 'dc_in',  x: UPB.DC_DC.width / 2, y: 0, type: 'dc' },
      dc_out: { id: 'dc_out', x: UPB.DC_DC.width / 2, y: UPB.DC_DC.height, type: 'dc' }
    }
  },

  fuse: {
    id: 'fuse',
    source: 'handrolled',
    label: 'Fuse',
    category: 'protection',
    nativeBox: { x: 0, y: 0, w: 18, h: 10 },
    defaultScale: 1,
    content: `
      <rect x="0" y="0" width="18" height="10" rx="0.8" fill="${S.fill}" stroke="${S.stroke}" stroke-width="1.2"/>
      <line x1="2" y1="5" x2="16" y2="5" stroke="${S.stroke}" stroke-width="0.8"/>
    `,
    terminals: {
      a: { id: 'a', x: 0,  y: 5, type: 'any' },
      b: { id: 'b', x: 18, y: 5, type: 'any' }
    }
  },

  ammeter: {
    id: 'ammeter',
    source: 'handrolled',
    label: 'Ammeter / CT',
    category: 'metering',
    // Hand-rolled horizontal CT: circle + 'A' with lead lines going LEFT
    // and RIGHT and terminals on the left/right edges. The upb-lea
    // Current_measurement glyph was vertically oriented (terminals at
    // top + bottom of an 8.3 x 13.6 box) which (a) sat sideways in the
    // horizontal MSB row and (b) at small size let the two handles
    // overlap on screen, making it impossible to grab them
    // independently when reconnecting.
    nativeBox: { x: 0, y: 0, w: 32, h: 18 },
    defaultScale: 1,
    content: `
      <line x1="0" y1="9" x2="7" y2="9" stroke="${S.stroke}" stroke-width="1.2"/>
      <line x1="25" y1="9" x2="32" y2="9" stroke="${S.stroke}" stroke-width="1.2"/>
      <circle cx="16" cy="9" r="7" fill="${S.fill}" stroke="${S.stroke}" stroke-width="1.2"/>
      <circle cx="16" cy="9" r="5.5" fill="none" stroke="${S.stroke}" stroke-width="0.5" opacity="0.6"/>
      <text x="16" y="12" font-size="8" text-anchor="middle" fill="${S.stroke}" font-family="Georgia, serif" font-style="italic" font-weight="600">A</text>
    `,
    terminals: {
      a: { id: 'a', x: 0,  y: 9, type: 'any' },
      b: { id: 'b', x: 32, y: 9, type: 'any' }
    }
  },

  grid: {
    id: 'grid',
    source: 'upb-lea',
    label: 'Grid',
    category: 'utility',
    nativeBox: { x: 0, y: 0, w: UPB.Grid.width, h: UPB.Grid.height },
    defaultScale: 3.5,
    content: upb('Grid'),
    terminals: {
      ac: { id: 'ac', x: UPB.Grid.width / 2, y: 0, type: 'ac' }
    }
  },

  dcIsolator: {
    id: 'dcIsolator',
    source: 'handrolled',
    label: 'DC Isolator',
    category: 'switchgear',
    nativeBox: { x: 0, y: 0, w: 30, h: 24 },
    defaultScale: 1,
    content: `
      <rect x="0" y="0" width="30" height="24" rx="2" fill="${S.fill}" stroke="${S.stroke}" stroke-width="1.2"/>
      <line x1="1" y1="12" x2="5" y2="12" stroke="${S.stroke}" stroke-width="1.2"/>
      <line x1="29" y1="12" x2="25" y2="12" stroke="${S.stroke}" stroke-width="1.2"/>
      <circle cx="5" cy="12" r="1.8" fill="${S.stroke}"/>
      <circle cx="25" cy="12" r="1.8" fill="${S.stroke}"/>
      <line x1="5" y1="12" x2="24" y2="5" stroke="${S.dcPos}" stroke-width="1.6" stroke-linecap="round"/>
    `,
    terminals: {
      a: { id: 'a', x: 0, y: 12, type: 'any' },
      b: { id: 'b', x: 30, y: 12, type: 'any' }
    }
  },

  acIsolator: {
    id: 'acIsolator',
    source: 'handrolled',
    label: 'AC Isolator',
    category: 'switchgear',
    nativeBox: { x: 0, y: 0, w: 26, h: 20 },
    defaultScale: 1,
    content: `
      <rect x="0" y="0" width="26" height="20" rx="1.5" fill="${S.fill}" stroke="${S.stroke}" stroke-width="1.2"/>
      <line x1="1" y1="10" x2="5" y2="10" stroke="${S.stroke}" stroke-width="1.2"/>
      <line x1="25" y1="10" x2="21" y2="10" stroke="${S.stroke}" stroke-width="1.2"/>
      <circle cx="5" cy="10" r="1.5" fill="${S.stroke}"/>
      <circle cx="21" cy="10" r="1.5" fill="${S.stroke}"/>
      <line x1="5" y1="10" x2="20" y2="4" stroke="${S.stroke}" stroke-width="1.6" stroke-linecap="round"/>
    `,
    terminals: {
      a: { id: 'a', x: 0, y: 10, type: 'ac' },
      b: { id: 'b', x: 26, y: 10, type: 'ac' }
    }
  },

  rcd: {
    id: 'rcd',
    source: 'handrolled',
    label: 'RCD',
    category: 'protection',
    nativeBox: { x: 0, y: 0, w: 26, h: 20 },
    defaultScale: 1,
    content: `
      <rect x="0" y="0" width="26" height="20" rx="1.5" fill="${S.fill}" stroke="${S.stroke}" stroke-width="1.2"/>
      <line x1="1" y1="10" x2="5" y2="10" stroke="${S.stroke}" stroke-width="1.2"/>
      <line x1="25" y1="10" x2="21" y2="10" stroke="${S.stroke}" stroke-width="1.2"/>
      <circle cx="5" cy="10" r="1.5" fill="${S.stroke}"/>
      <circle cx="21" cy="10" r="1.5" fill="${S.stroke}"/>
      <line x1="5" y1="10" x2="20" y2="4" stroke="${S.stroke}" stroke-width="1.6" stroke-linecap="round"/>
      <circle cx="13" cy="15.5" r="1.8" fill="none" stroke="${S.stroke}" stroke-width="0.8"/>
      <text x="13" y="19" font-size="4.5" text-anchor="middle" fill="${S.strokeDim}" font-weight="700" font-family="Arial">RCD</text>
    `,
    terminals: {
      a: { id: 'a', x: 0, y: 10, type: 'ac' },
      b: { id: 'b', x: 26, y: 10, type: 'ac' }
    }
  },

  mcb: {
    id: 'mcb',
    source: 'handrolled',
    label: 'MCB',
    category: 'protection',
    nativeBox: { x: 0, y: 0, w: 26, h: 20 },
    defaultScale: 1,
    content: `
      <rect x="0" y="0" width="26" height="20" rx="1.5" fill="${S.fill}" stroke="${S.stroke}" stroke-width="1.2"/>
      <line x1="1" y1="10" x2="5" y2="10" stroke="${S.stroke}" stroke-width="1.2"/>
      <line x1="25" y1="10" x2="21" y2="10" stroke="${S.stroke}" stroke-width="1.2"/>
      <circle cx="5" cy="10" r="1.5" fill="${S.stroke}"/>
      <circle cx="21" cy="10" r="1.5" fill="${S.stroke}"/>
      <line x1="5" y1="10" x2="20" y2="4" stroke="${S.stroke}" stroke-width="1.6" stroke-linecap="round"/>
      <path d="M 9,7 Q 13,2 17,7" fill="none" stroke="${S.stroke}" stroke-width="0.8"/>
      <text x="13" y="17.5" font-size="4.5" text-anchor="middle" fill="${S.strokeDim}" font-weight="700" font-family="Arial">MCB</text>
    `,
    terminals: {
      a: { id: 'a', x: 0, y: 10, type: 'ac' },
      b: { id: 'b', x: 26, y: 10, type: 'ac' }
    }
  },

  serviceFuse: {
    id: 'serviceFuse',
    source: 'handrolled',
    label: 'Service Fuse',
    category: 'protection',
    nativeBox: { x: 0, y: 0, w: 22, h: 10 },
    defaultScale: 1,
    content: `
      <rect x="2" y="1" width="18" height="8" rx="0.8" fill="${S.fill}" stroke="${S.stroke}" stroke-width="1.2"/>
      <rect x="0" y="2.5" width="2.4" height="5" fill="${S.stroke}" stroke="none"/>
      <rect x="19.6" y="2.5" width="2.4" height="5" fill="${S.stroke}" stroke="none"/>
      <line x1="3" y1="5" x2="19" y2="5" stroke="${S.stroke}" stroke-width="0.8"/>
      <text x="11" y="6.7" font-size="3.5" text-anchor="middle" fill="${S.strokeDim}" font-weight="700" font-family="Arial">F</text>
    `,
    terminals: {
      a: { id: 'a', x: 0,  y: 5, type: 'any' },
      b: { id: 'b', x: 22, y: 5, type: 'any' }
    }
  },

  meter: {
    id: 'meter',
    source: 'handrolled',
    label: 'Meter',
    category: 'metering',
    nativeBox: { x: 0, y: 0, w: 24, h: 24 },
    defaultScale: 1,
    content: `
      <circle cx="12" cy="12" r="11" fill="${S.fill}" stroke="${S.stroke}" stroke-width="1.2"/>
      <circle cx="12" cy="12" r="9" fill="none" stroke="${S.stroke}" stroke-width="0.5" opacity="0.6"/>
      <text x="12" y="15.5" font-size="11" text-anchor="middle" fill="${S.stroke}" font-family="Georgia, serif" font-style="italic" font-weight="600">M</text>
    `,
    terminals: {
      a: { id: 'a', x: 0, y: 12, type: 'any' },
      b: { id: 'b', x: 24, y: 12, type: 'any' }
    }
  },

  transformer: {
    id: 'transformer',
    source: 'handrolled',
    label: 'Transformer',
    category: 'utility',
    nativeBox: { x: 0, y: 0, w: 22, h: 14 },
    defaultScale: 1,
    content: `
      <circle cx="8" cy="7" r="7" fill="${S.fill}" stroke="${S.stroke}" stroke-width="1.2"/>
      <circle cx="14" cy="7" r="7" fill="none" stroke="${S.stroke}" stroke-width="1.2"/>
    `,
    terminals: {
      a: { id: 'a', x: 0, y: 7, type: 'any' },
      b: { id: 'b', x: 22, y: 7, type: 'any' }
    }
  },

  ground: {
    id: 'ground',
    source: 'handrolled',
    label: 'Ground',
    category: 'misc',
    nativeBox: { x: 0, y: 0, w: 18, h: 14 },
    defaultScale: 1,
    content: `
      <line x1="9" y1="0" x2="9" y2="4" stroke="${S.stroke}" stroke-width="1.2"/>
      <line x1="0" y1="4" x2="18" y2="4" stroke="${S.stroke}" stroke-width="1.6"/>
      <line x1="3" y1="7" x2="15" y2="7" stroke="${S.stroke}" stroke-width="1.2"/>
      <line x1="6" y1="10" x2="12" y2="10" stroke="${S.stroke}" stroke-width="1.2"/>
    `,
    terminals: {
      earth: { id: 'earth', x: 9, y: 0, type: 'earth' }
    }
  },

  panel: {
    id: 'panel',
    source: 'upb-lea',
    label: 'PV Panel',
    category: 'pv',
    nativeBox: { x: 0, y: 0, w: UPB.PV.width, h: UPB.PV.height },
    defaultScale: 2.2,
    content: upb('PV'),
    terminals: {
      'pos': { id: 'pos', x: UPB.PV.width / 2, y: 0, type: 'dc+' },
      'neg': { id: 'neg', x: UPB.PV.width / 2, y: UPB.PV.height, type: 'dc-' }
    }
  },

  panelString: {
    id: 'panelString',
    source: 'handrolled',
    label: 'Panel String',
    category: 'pv',
    nativeBox: { x: 0, y: 0, w: 120, h: 30 },
    defaultScale: 1,
    content: '',
    terminals: {
      'pos': { id: 'pos', x: 0, y: 15, type: 'dc+' },
      'neg': { id: 'neg', x: 120, y: 15, type: 'dc-' }
    }
  },

  battery: {
    id: 'battery',
    source: 'handrolled',
    label: 'Battery',
    category: 'storage',
    nativeBox: { x: 0, y: 0, w: 56, h: 28 },
    defaultScale: 1,
    content: `
      <rect x="0" y="0" width="56" height="28" rx="2.5" fill="${S.fillTint}" stroke="${S.stroke}" stroke-width="1.2"/>
      <line x1="6" y1="5" x2="6" y2="23" stroke="${S.stroke}" stroke-width="1.6" stroke-linecap="round"/>
      <line x1="11.5" y1="9" x2="11.5" y2="19" stroke="${S.stroke}" stroke-width="1.2" stroke-linecap="round"/>
      <line x1="17" y1="5" x2="17" y2="23" stroke="${S.stroke}" stroke-width="1.6" stroke-linecap="round"/>
      <line x1="22.5" y1="9" x2="22.5" y2="19" stroke="${S.stroke}" stroke-width="1.2" stroke-linecap="round"/>
      <line x1="28" y1="5" x2="28" y2="23" stroke="${S.stroke}" stroke-width="1.6" stroke-linecap="round"/>
      <line x1="33.5" y1="9" x2="33.5" y2="19" stroke="${S.stroke}" stroke-width="1.2" stroke-linecap="round"/>
      <line x1="39" y1="5" x2="39" y2="23" stroke="${S.stroke}" stroke-width="1.6" stroke-linecap="round"/>
      <line x1="44.5" y1="9" x2="44.5" y2="19" stroke="${S.stroke}" stroke-width="1.2" stroke-linecap="round"/>
      <text x="4" y="7" font-size="6" fill="${S.dcPos}" font-weight="700" font-family="Arial">+</text>
      <text x="52" y="7" font-size="6" fill="${S.stroke}" font-weight="700" text-anchor="end" font-family="Arial">−</text>
    `,
    terminals: {
      'pos': { id: 'pos', x: 0,  y: 14, type: 'dc+' },
      'neg': { id: 'neg', x: 56, y: 14, type: 'dc-' }
    }
  },

  backupGateway: {
    id: 'backupGateway',
    source: 'handrolled',
    label: 'Backup Gateway',
    category: 'switchgear',
    nativeBox: { x: 0, y: 0, w: 48, h: 36 },
    defaultScale: 1,
    content: `
      <rect x="0" y="2" width="48" height="32" rx="2" fill="${S.fillTint}" stroke="${S.stroke}" stroke-width="1.2"/>
      <line x1="12" y1="2" x2="12" y2="10" stroke="${S.stroke}" stroke-width="1.2"/>
      <line x1="36" y1="2" x2="36" y2="10" stroke="${S.stroke}" stroke-width="1.2"/>
      <circle cx="12" cy="10" r="1.3" fill="${S.stroke}"/>
      <circle cx="36" cy="10" r="1.3" fill="${S.stroke}"/>
      <circle cx="24" cy="22" r="1.5" fill="${S.stroke}"/>
      <line x1="12" y1="10" x2="24" y2="22" stroke="${S.stroke}" stroke-width="1.6" stroke-linecap="round"/>
      <line x1="24" y1="22" x2="36" y2="10" stroke="${S.stroke}" stroke-width="0.8" stroke-dasharray="1.5 1.5" opacity="0.5"/>
      <line x1="24" y1="22" x2="24" y2="34" stroke="${S.stroke}" stroke-width="1.2"/>
      <text x="24" y="32" font-size="5.5" text-anchor="middle" fill="${S.strokeDim}" font-weight="700" font-family="Arial">ATS</text>
    `,
    terminals: {
      grid_in: { id: 'grid_in', x: 12, y: 2, type: 'ac' },
      bat_in:  { id: 'bat_in',  x: 36, y: 2, type: 'ac' },
      out:     { id: 'out',     x: 24, y: 34, type: 'ac' }
    }
  },

  optimiser: {
    id: 'optimiser',
    source: 'handrolled',
    label: 'Optimiser',
    category: 'inverter',
    nativeBox: { x: 0, y: 0, w: 14, h: 9 },
    defaultScale: 1,
    content: `
      <rect x="0" y="0" width="14" height="9" rx="1" fill="${S.badgeAmberBg}" stroke="${S.badgeAmber}" stroke-width="0.8"/>
      <text x="7" y="6.5" font-size="6" text-anchor="middle" fill="${S.badgeAmber}" font-weight="700" font-family="Arial">OPT</text>
    `,
    terminals: {
      a: { id: 'a', x: 0, y: 4.5, type: 'any' },
      b: { id: 'b', x: 14, y: 4.5, type: 'any' }
    }
  }
};

export function symbolsForPalette(): { category: string; symbols: SymbolDef[] }[] {
  const groups: Record<string, SymbolDef[]> = {};
  for (const sym of Object.values(SYMBOLS)) {
    // The bare `panel` symbol represents a single PV module. Useless
    // on its own in an SLD: panels live in strings, strings live on
    // MPPTs, MPPTs live on inverters. The draggable equivalent is
    // panelString (a composite "× N panels per string" block).
    if (sym.id === 'panel') continue;
    if (!groups[sym.category]) groups[sym.category] = [];
    groups[sym.category].push(sym);
  }
  const order = ['inverter', 'pv', 'storage', 'switchgear', 'protection', 'metering', 'utility', 'misc'];
  return order.filter(c => groups[c]).map(c => ({ category: c, symbols: groups[c] }));
}

// ============================================================
// PANEL STRING RENDERER
// ============================================================

const PANEL_W = 14;
const PANEL_H = 28;
const PANEL_GAP = 6;
const STRING_EXPLICIT_THRESHOLD = 4;

const COLOR_DC_POS = '#c62828';
const COLOR_DC_NEG = '#0f172a';
const COLOR_EARTH  = '#16a34a';
const RAIL_WIDTH   = 1.2;

export interface PanelStringRender {
  content: string;
  nativeBox: { w: number; h: number };
  terminals: {
    pos: { x: number; y: number };
    neg: { x: number; y: number };
  };
}

/**
 * Draw a single panel as the standard IEC-style PV module symbol:
 * tall rectangle with an inverted-V notch cut from the top edge.
 */
function drawPanelRect(x: number, y: number, _isFirst: boolean): string {
  const notchDepth = 6;
  const cx = x + PANEL_W / 2;
  const d = [
    `M ${x} ${y + PANEL_H}`,
    `L ${x + PANEL_W} ${y + PANEL_H}`,
    `L ${x + PANEL_W} ${y}`,
    `L ${cx} ${y + notchDepth}`,
    `L ${x} ${y}`,
    'Z'
  ].join(' ');
  return (
    `<path d="${d}" fill="#fff" stroke="${COLOR_DC_NEG}" ` +
    `stroke-width="1.2" stroke-linejoin="miter"/>`
  );
}

function drawEarthStub(cx: number, topY: number): string {
  return `
    <line x1="${cx}" y1="${topY}" x2="${cx}" y2="${topY + 4}" stroke="${COLOR_EARTH}" stroke-width="${RAIL_WIDTH}"/>
    <line x1="${cx - 6}" y1="${topY + 4}" x2="${cx + 6}" y2="${topY + 4}" stroke="${COLOR_EARTH}" stroke-width="1.6"/>
    <line x1="${cx - 4}" y1="${topY + 6}" x2="${cx + 4}" y2="${topY + 6}" stroke="${COLOR_EARTH}" stroke-width="${RAIL_WIDTH}"/>
    <line x1="${cx - 2}" y1="${topY + 8}" x2="${cx + 2}" y2="${topY + 8}" stroke="${COLOR_EARTH}" stroke-width="${RAIL_WIDTH}"/>`;
}

export function renderPanelString(
  count: number,
  label?: string,
  totalPanels?: number,
  /**
   * When true, the composite gets a dashed violet boundary + an
   * "EXISTING" badge in the top-left corner — standard SLD convention
   * for marking pre-existing (retrofit) arrays so they're distinct
   * from newly added equipment.
   */
  isExisting?: boolean,
): PanelStringRender {
  const labelH = label ? 14 : 0;
  const railSpacing = PANEL_H + 8;
  const topRailY = labelH + 4;
  const botRailY = topRailY + railSpacing;
  const panelY = topRailY + (railSpacing - PANEL_H) / 2;

  const explicit = count <= STRING_EXPLICIT_THRESHOLD;

  // Wider label area when we need to fit the "220 total" badge inline
  // to the right of the per-string count. Without this, the total either
  // stacks under "panels" (overlap) or runs off the composite's right edge.
  const isCollapsed = totalPanels !== undefined && totalPanels !== count;
  const labelW = isCollapsed ? 110 : 50;

  const parts: string[] = [];

  const leftPad = 14;
  let panelXs: number[] = [];
  let dotsRange: { start: number; end: number } | null = null;
  let totalW = 0;

  if (explicit) {
    for (let i = 0; i < count; i++) {
      panelXs.push(leftPad + i * (PANEL_W + PANEL_GAP));
    }
    const lastX = panelXs[panelXs.length - 1];
    totalW = lastX + PANEL_W + leftPad + 50;
  } else {
    const dotsGap = 32;
    panelXs.push(leftPad);
    panelXs.push(leftPad + (PANEL_W + PANEL_GAP));
    const dotsStart = panelXs[1] + PANEL_W + 4;
    const dotsEnd = dotsStart + dotsGap - 4;
    panelXs.push(dotsEnd + 4);
    dotsRange = { start: dotsStart, end: dotsEnd };
    const lastX = panelXs[panelXs.length - 1];
    totalW = lastX + PANEL_W + 60 + 14;
  }

  panelXs.forEach((px, i) => {
    parts.push(drawPanelRect(px, panelY, i === 0));
  });

  const firstCx = panelXs[0] + PANEL_W / 2;
  const lastCx  = panelXs[panelXs.length - 1] + PANEL_W / 2;

  parts.push(`<line x1="0" y1="${panelY}" x2="${firstCx}" y2="${panelY}" stroke="${COLOR_DC_POS}" stroke-width="${RAIL_WIDTH}"/>`);

  if (explicit) {
    parts.push(`<line x1="${firstCx}" y1="${panelY}" x2="${lastCx}" y2="${panelY}" stroke="${COLOR_DC_POS}" stroke-width="${RAIL_WIDTH}"/>`);
  } else if (dotsRange) {
    const secondCx = panelXs[1] + PANEL_W / 2;
    parts.push(`<line x1="${firstCx}" y1="${panelY}" x2="${secondCx}" y2="${panelY}" stroke="${COLOR_DC_POS}" stroke-width="${RAIL_WIDTH}"/>`);
    parts.push(`<line x1="${dotsRange.start}" y1="${panelY}" x2="${dotsRange.end}" y2="${panelY}" stroke="${COLOR_DC_POS}" stroke-width="${RAIL_WIDTH}" stroke-dasharray="3 3"/>`);
    parts.push(`<line x1="${dotsRange.end}" y1="${panelY}" x2="${lastCx}" y2="${panelY}" stroke="${COLOR_DC_POS}" stroke-width="${RAIL_WIDTH}"/>`);
  }

  const botEdge = panelY + PANEL_H;
  if (explicit) {
    parts.push(`<line x1="${firstCx}" y1="${botEdge}" x2="${lastCx}" y2="${botEdge}" stroke="${COLOR_DC_NEG}" stroke-width="${RAIL_WIDTH}"/>`);
  } else if (dotsRange) {
    const secondCx = panelXs[1] + PANEL_W / 2;
    parts.push(`<line x1="${firstCx}" y1="${botEdge}" x2="${secondCx}" y2="${botEdge}" stroke="${COLOR_DC_NEG}" stroke-width="${RAIL_WIDTH}"/>`);
    parts.push(`<line x1="${dotsRange.start}" y1="${botEdge}" x2="${dotsRange.end}" y2="${botEdge}" stroke="${COLOR_DC_NEG}" stroke-width="${RAIL_WIDTH}" stroke-dasharray="3 3"/>`);
    parts.push(`<line x1="${dotsRange.end}" y1="${botEdge}" x2="${lastCx}" y2="${botEdge}" stroke="${COLOR_DC_NEG}" stroke-width="${RAIL_WIDTH}"/>`);
  }

  const labelX = lastCx + PANEL_W / 2 + 4;
  const earthCx = labelX + labelW + 6;

  parts.push(`<line x1="${lastCx}" y1="${botEdge}" x2="${earthCx}" y2="${botEdge}" stroke="${COLOR_DC_NEG}" stroke-width="${RAIL_WIDTH}"/>`);

  parts.push(drawEarthStub(earthCx, botEdge));

  if (dotsRange) {
    const dotsMidX = (dotsRange.start + dotsRange.end) / 2;
    const dotsMidY = (panelY + botEdge) / 2 + 3;
    parts.push(`<text x="${dotsMidX}" y="${dotsMidY}" font-size="14" text-anchor="middle" fill="${COLOR_DC_NEG}" font-weight="700" font-family="Arial">···</text>`);
  }

  const countY = (panelY + botEdge) / 2;
  // For collapsed lanes (a single visual lane represents many parallel
  // strings or many identical MPPTs) the per-string `count` is misleading
  // on its own. Show the per-string number first, then a "TOTAL N panels"
  // badge to the RIGHT — same row, no stacking, neutral colour. The
  // earlier red "Sigma N" version below the panels label both overlapped
  // and looked like a warning.
  if (isCollapsed) {
    // Per-string number on the left
    parts.push(`<text x="${labelX}" y="${countY - 2}" font-size="10" fill="${COLOR_DC_NEG}" font-weight="700" font-family="Arial">× ${count}</text>`);
    parts.push(`<text x="${labelX}" y="${countY + 8}" font-size="7.5" fill="#64748b" font-family="Arial">per string</text>`);
    // Total badge to the right of the per-string count, vertically centred.
    const totalX = labelX + 36;
    parts.push(`<text x="${totalX}" y="${countY - 2}" font-size="7" fill="#64748b" font-weight="600" font-family="Arial" letter-spacing="0.5">TOTAL</text>`);
    parts.push(`<text x="${totalX}" y="${countY + 9}" font-size="11" fill="${COLOR_DC_NEG}" font-weight="700" font-family="Arial">${totalPanels} <tspan font-size="7.5" font-weight="500" fill="#64748b">panels</tspan></text>`);
  } else {
    parts.push(`<text x="${labelX}" y="${countY - 3}" font-size="11" fill="${COLOR_DC_NEG}" font-weight="700" font-family="Arial">× ${count}</text>`);
    parts.push(`<text x="${labelX}" y="${countY + 9}" font-size="8.5" fill="#64748b" font-family="Arial">panels</text>`);
  }

  if (label) {
    const labelCenterX = totalW / 2;
    parts.push(`<text x="${labelCenterX}" y="${labelH - 2}" font-size="10" font-weight="600" text-anchor="middle" fill="${COLOR_DC_NEG}" font-family="Arial">${label}</text>`);
  }

  const totalH = botRailY + 4;

  // (Existing-array boundary used to be drawn inline here. It now
  // lives in SldSymbolNode as a universal overlay so the same dashed
  // border + EXISTING badge applies to inverters, batteries and any
  // other symbol marked isExisting — not just panel strings. Drawing
  // it once at the wrapper level avoids label-position collisions
  // between the centred lane label and the EXISTING badge.)
  void isExisting;   // signature retained for backward compat

  return {
    content: parts.join('\n'),
    nativeBox: { w: totalW, h: totalH },
    terminals: {
      pos: { x: 0,        y: panelY  },
      neg: { x: earthCx,  y: botEdge }
    }
  };
}
